import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Award, Users, Check, X, Copy, Share2, TrendingUp,
  Target, Clock, Loader2, ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NomineeDashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, token } = useAuth();
  const { toast } = useToast();
  
  const [nomination, setNomination] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [postCopied, setPostCopied] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    } else if (isAuthenticated) {
      fetchDashboard();
    }
  }, [authLoading, isAuthenticated]);

  const fetchDashboard = async () => {
    try {
      // Get nomination
      const nomResponse = await fetch(`${BACKEND_URL}/api/awards/my-nomination`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const nomData = await nomResponse.json();
      
      if (!nomData.has_nomination) {
        toast({
          title: 'No Nomination Found',
          description: 'You haven\'t submitted a nomination yet',
          variant: 'destructive'
        });
        navigate('/awards/nominate');
        return;
      }
      
      setNomination(nomData.nomination);
      
      // Get stats
      const statsResponse = await fetch(
        `${BACKEND_URL}/api/awards/nominee/${nomData.nomination.unique_url}/stats`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const statsData = await statsResponse.json();
      
      if (statsData.success) {
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/awards/nominee/${nomination.unique_url}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast({
      title: 'Link Copied!',
      description: 'Share this link to gather more votes'
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    setShowShare(true);
  };

  const shareLink = nomination ? `${window.location.origin}/awards/nominee/${nomination.unique_url}` : '';
  const sharePost = `🏆 I've been nominated for the Global TA Awards 2026 on RecruiterIQ!\n\nYour vote means the world to me — it only takes 30 seconds.\n\n🗳️ Vote here: ${shareLink}\n\n#TAAwards #TalentAcquisition #RecruiterIQ`;

  const copyPost = () => {
    navigator.clipboard.writeText(sharePost);
    setPostCopied(true);
    toast({ title: 'Post copied!', description: 'Paste it on LinkedIn or anywhere you like.' });
    setTimeout(() => setPostCopied(false), 2000);
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  const voteTypes = [
    { key: 'candidate', label: 'Candidates', icon: '👤', color: 'bg-blue-500' },
    { key: 'peer', label: 'Industry Peers', icon: '🤝', color: 'bg-green-500' },
    { key: 'senior_junior', label: 'Seniors/Juniors', icon: '👔', color: 'bg-purple-500' },
    { key: 'manager', label: 'Managers', icon: '💼', color: 'bg-orange-500' },
    { key: 'hr', label: 'HR Team', icon: '🎯', color: 'bg-pink-500' }
  ];

  const categoryInfo = {
    rookie: 'Best TA Rookie Award',
    emerging_leader: 'Best Emerging TA Leader Award',
    excellence: 'Best TA Excellence Award',
    manager: 'Best TA Manager Award',
    senior_leader: 'Best TA Senior Leader Award'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <Button onClick={() => navigate('/awards')} variant="outline" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Awards
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <Award className="w-16 h-16 mx-auto mb-4 text-purple-600" />
          <h1 className="text-5xl font-bold text-slate-900 mb-2">My Award Dashboard</h1>
          <p className="text-xl text-slate-600">{categoryInfo[nomination.category]}</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="shadow-lg">
            <CardContent className="pt-6 text-center">
              <Users className="w-12 h-12 mx-auto mb-2 text-blue-600" />
              <div className="text-5xl font-bold text-slate-900">{stats?.total_votes || 0}</div>
              <p className="text-slate-600 font-semibold">Total Votes</p>
            </CardContent>
          </Card>
          
          <Card className={`shadow-lg border-2 ${stats?.is_qualified ? 'border-green-500 bg-green-50' : 'border-orange-300 bg-orange-50'}`}>
            <CardContent className="pt-6 text-center">
              {stats?.is_qualified ? (
                <Check className="w-12 h-12 mx-auto mb-2 text-green-600" />
              ) : (
                <Clock className="w-12 h-12 mx-auto mb-2 text-orange-600" />
              )}
              <div className="text-3xl font-bold text-slate-900 mb-1">
                {stats?.is_qualified ? 'Qualified!' : 'In Progress'}
              </div>
              <p className="text-slate-600 font-semibold">
                {stats?.is_qualified ? '23/23 Votes Met' : `${stats?.total_votes || 0}/23 Votes`}
              </p>
            </CardContent>
          </Card>

          {stats?.rank && (
            <Card className="shadow-lg border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-orange-50">
              <CardContent className="pt-6 text-center">
                <Trophy className="w-12 h-12 mx-auto mb-2 text-yellow-600" />
                <div className="text-5xl font-bold text-slate-900">#{stats.rank}</div>
                <p className="text-slate-600 font-semibold">Current Rank</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Qualification Progress */}
        <Card className="mb-8 shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Target className="w-6 h-6 text-purple-600" />
              Qualification Progress
            </CardTitle>
            <p className="text-slate-600">Track your votes by category</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {voteTypes.map((type) => {
              const progress = stats?.qualification_progress?.[type.key];
              const current = progress?.current || 0;
              const required = progress?.required || 0;
              const percentage = progress?.percentage || 0;
              const isMet = current >= required;

              return (
                <div key={type.key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{type.icon}</span>
                      <span className="font-semibold text-slate-900">{type.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${isMet ? 'bg-green-500' : 'bg-slate-200 text-slate-700'}`}>
                        {current}/{required}
                      </Badge>
                      {isMet && <Check className="w-5 h-5 text-green-600" />}
                    </div>
                  </div>
                  <Progress value={percentage} className={`h-3 ${isMet ? '[&>div]:bg-green-500' : ''}`} />
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Share Section */}
        <Card className="shadow-lg border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Share2 className="w-6 h-6 text-purple-600" />
              Share Your Nomination
            </CardTitle>
            <p className="text-slate-600">Spread the word and gather more votes!</p>
          </CardHeader>
          <CardContent>
            <div className="bg-white border-2 border-purple-200 rounded-lg p-4 mb-4">
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">Your Unique Link</Label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={`${window.location.origin}/awards/nominee/${nomination?.unique_url}`}
                  readOnly
                  className="flex-1 px-3 py-2 border-2 border-slate-300 rounded bg-slate-50 text-slate-700 font-mono text-sm"
                />
                <Button onClick={handleCopyLink} className="bg-purple-600 hover:bg-purple-700">
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button onClick={handleShare} size="lg" className="bg-blue-600 hover:bg-blue-700 font-bold">
                <Share2 className="w-5 h-5 mr-2" />
                Share on Social Media
              </Button>
              <Button
                onClick={() => window.open(`/awards/nominee/${nomination?.unique_url}`, '_blank')}
                size="lg"
                variant="outline"
                className="border-2 border-purple-600 text-purple-600 hover:bg-purple-50 font-bold"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Preview My Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Share popup */}
      {showShare && (
        <div className="fixed inset-0 z-[9998] bg-black/50 flex items-center justify-center p-4" data-testid="share-popup-overlay" onClick={() => setShowShare(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" data-testid="share-popup" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
              <h3 className="font-bold text-lg flex items-center gap-2"><Share2 className="w-5 h-5" /> Share Your Nomination</h3>
              <button data-testid="share-popup-close" onClick={() => setShowShare(false)} className="p-1 rounded hover:bg-white/20"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <p className="text-sm font-semibold text-slate-700 mb-2">Ready-to-post update:</p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-slate-700 whitespace-pre-wrap mb-4" data-testid="share-post-text">{sharePost}</div>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  data-testid="share-linkedin"
                  onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`, '_blank', 'noopener,width=600,height=600')}
                  className="bg-[#0A66C2] hover:bg-[#084d92] font-bold"
                >
                  Share on LinkedIn
                </Button>
                <Button
                  data-testid="share-x"
                  onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(sharePost)}`, '_blank', 'noopener,width=600,height=600')}
                  className="bg-slate-900 hover:bg-slate-700 font-bold"
                >
                  Share on X
                </Button>
                <Button
                  data-testid="share-whatsapp"
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(sharePost)}`, '_blank', 'noopener')}
                  className="bg-[#25D366] hover:bg-[#1da851] font-bold"
                >
                  WhatsApp
                </Button>
                <Button data-testid="share-copy-post" onClick={copyPost} variant="outline" className="border-2 border-purple-500 text-purple-600 font-bold">
                  {postCopied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />} Copy Post
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-3">💡 Tip: LinkedIn shares get the most votes — paste the copied post for maximum reach.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NomineeDashboardPage;
